import React, { useState, useEffect, useRef } from 'react';
import ConfirmModal from '@/components/ConfirmModal';
import Toast from '@/components/Toast';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Card } from '@/components/Card';
import { Button } from '@/components/Button';
import { articleAdminApi } from '@/features/admin/articleAdminApi';
import { Category } from '@/types/category';
import { Article } from '@/types/article';
import LoadingOverlay from '@/components/LoadingOverlay';
import { useAuth } from '@/contexts/AuthContext';
import {
  useAdminCategoriesByCountryQuery,
  useAdminTopicsAsCategoriesQuery,
} from '@hooks/useConfigureQueries';

export const ExtractedWordsReview: React.FC = () => {
  const { user } = useAuth();
  const { country, id: articleId } = useParams<{ country: string; id: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [article, setArticle] = useState<Article | null>(null);
  const [notification, setNotification] = useState<{
    message: string;
    type?: 'success' | 'error' | 'info';
  } | null>(null);
  const [showPublishConfirm, setShowApproveConfirm] = useState(false);
  const notifTimeout = useRef<number | null>(null);
  const { data: adminCountryCategories = [] } = useAdminCategoriesByCountryQuery(
    country,
    Boolean(country)
  );
  const { data: adminTopicCategories = [] } = useAdminTopicsAsCategoriesQuery(!country);
  const categories: Category[] = country ? adminCountryCategories : adminTopicCategories;

  const getLevelLabel = (level: 'A' | 'B' | 'C'): string => {
    if (level === 'A') return 'Easy';
    if (level === 'B') return 'Medium';
    return 'Hard';
  };

  const showNotification = (
    message: string,
    type: 'success' | 'error' | 'info' = 'info',
    duration = 3000
  ) => {
    setNotification({ message, type });
    if (notifTimeout.current) window.clearTimeout(notifTimeout.current);
    notifTimeout.current = window.setTimeout(() => setNotification(null), duration);
  };

  useEffect(() => {
    return () => {
      if (notifTimeout.current) window.clearTimeout(notifTimeout.current);
    };
  }, []);

  const fetchArticleDetail = async (country: string, articleId: string) => {
    setIsLoading(true);
    try {
      const data = await articleAdminApi.getById(country, articleId);
      setArticle(data);
    } catch (error) {
      showNotification('Failed to fetch article details', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (articleId) {
      fetchArticleDetail(country || '', articleId);
    }
  }, [country, articleId]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setArticle(prev => (prev ? { ...prev, [e.target.name]: e.target.value } : prev));
  };

  const handleExtractedWordsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const lines = e.target.value.split('\n');
    setArticle(prev => (prev ? { ...prev, [e.target.name]: lines } : prev));
  };

  const cleanExtractedWords = (words: string[]) => {
    // Get unique words (case-insensitive)
    const uniqueWords = Array.from(new Set(words.map(w => w.trim().toLowerCase()).filter(Boolean)));

    // Return words in their original casing but unique
    const result = Array.from(
      new Set(
        uniqueWords.map(uw => {
          return words.find(w => w.trim().toLowerCase() === uw) || uw;
        })
      )
    );

    return result;
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      // Clean extracted words
      if (country?.toUpperCase() !== 'VI') {
        article!.extractWordsALevel = cleanExtractedWords(article!.extractWordsALevel || []);
        article!.extractWordsBLevel = cleanExtractedWords(article!.extractWordsBLevel || []);
        article!.extractWordsCLevel = cleanExtractedWords(article!.extractWordsCLevel || []);
      } else {
        article!.extractWordsALevelUS = cleanExtractedWords(article!.extractWordsALevelUS || []);
        article!.extractWordsBLevelUS = cleanExtractedWords(article!.extractWordsBLevelUS || []);
        article!.extractWordsCLevelUS = cleanExtractedWords(article!.extractWordsCLevelUS || []);
      }

      // This process may take a while due to the dictionary generation, so don't await the response and
      // navigate back to the list with a notification, the list page will handle refetching the updated article details
      articleAdminApi.update(country!, articleId!, article!);
      setTimeout(() => {
        setIsLoading(false);
        navigate('/editor/articles', {
          state: {
            notification: { message: 'Article saved successfully!', type: 'success' },
            activeTab: 'words',
          },
        });
      }, 2000);
    } catch (error) {
      showNotification('Failed to save article', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = () => {
    setShowApproveConfirm(true);
  };

  const performApprove = async () => {
    if (!article) return;
    setShowApproveConfirm(false);
    article.isExtractedWordsReviewed = true;
    article.generateDictionary = true;
    article.extractedWordsReviewedBy = user?.name || 'Unknown';
    handleSave();
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      {isLoading && <LoadingOverlay text="Generating dictionary for the extracted words..." />}
      <Toast notification={notification} onClose={() => setNotification(null)} />
      <ConfirmModal
        open={showPublishConfirm}
        title="Confirm Approve Extracted Words"
        description="Are you sure you want to approve this article extracted words?"
        confirmText="Yes, approve"
        cancelText="Cancel"
        isLoading={isLoading}
        onConfirm={performApprove}
        onCancel={() => setShowApproveConfirm(false)}
      />
      <div className="mb-8 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Review Extracted Words</h1>
          <p className="text-gray-600 mt-2">
            Make changes to the article content and extracted words
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => navigate('/editor/articles', { state: { activeTab: 'words' } })}
        >
          ← Back to Articles
        </Button>
      </div>

      <Card className="p-6">
        <form className="space-y-6">
          <div>
            <label htmlFor="slug" className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            {article?.isExtractedWordsReviewed && (
              <span className="px-2 py-1 text-xs font-semibold bg-green-100 text-green-800">
                Reviewed
              </span>
            )}
            {!article?.isExtractedWordsReviewed && (
              <span className="px-2 py-1 text-xs font-semibold bg-red-100 text-red-800">
                Pending Review
              </span>
            )}
          </div>

          {country && country.toUpperCase() !== 'VI' && (
            <div>
              <div>
                <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                  Title
                </label>
                <span>{article?.title ?? ''}</span>
              </div>

              <div className="grid grid-cols-2 gap-6 items-start">
                <div>
                  <label
                    htmlFor="textALevelEN"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Content level A ({getLevelLabel('A')})
                  </label>
                  <textarea
                    id="textALevelEN"
                    name="textALevelEN"
                    value={article?.textALevelEN ?? ''}
                    rows={15}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm mt-10"
                    disabled={true}
                  />
                </div>

                <div>
                  <label
                    htmlFor="extractWordsALevel"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Title and content level A ({getLevelLabel('A')}) Extracted Words
                  </label>
                  <span className="block text-xs text-gray-500 mb-2">
                    Keep the extracted words/phrases consistent with the content in order they
                    appear. Separate by new line. Each word/phrase is unique.
                  </span>
                  <textarea
                    id="extractWordsALevel"
                    name="extractWordsALevel"
                    value={article?.extractWordsALevel?.join('\n') ?? ''}
                    onChange={handleExtractedWordsChange}
                    rows={15}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6 items-start">
                <div>
                  <label
                    htmlFor="textBLevelEN"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Content level B ({getLevelLabel('B')})
                  </label>
                  <textarea
                    id="textBLevelEN"
                    name="textBLevelEN"
                    value={article?.textBLevelEN ?? ''}
                    rows={15}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm mt-10"
                    disabled={true}
                  />
                </div>

                <div>
                  <label
                    htmlFor="extractWordsBLevel"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Title and content level B ({getLevelLabel('B')}) Extracted Words
                  </label>
                  <span className="block text-xs text-gray-500 mb-2">
                    Keep the extracted words/phrases consistent with the content in the order they
                    appear. Separate by new line. Each word/phrase is unique.
                  </span>

                  <textarea
                    id="extractWordsBLevel"
                    name="extractWordsBLevel"
                    value={article?.extractWordsBLevel?.join('\n') ?? ''}
                    onChange={handleExtractedWordsChange}
                    rows={15}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6 items-start">
                <div>
                  <label
                    htmlFor="textCLevelEN"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Content level C ({getLevelLabel('C')})
                  </label>
                  <textarea
                    id="textCLevelEN"
                    name="textCLevelEN"
                    value={article?.textCLevelEN ?? ''}
                    rows={15}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm mt-10"
                    disabled={true}
                  />
                </div>

                <div>
                  <label
                    htmlFor="extractWordsCLevel"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Title and content level C ({getLevelLabel('C')}) Extracted Words
                  </label>
                  <span className="block text-xs text-gray-500 mb-2">
                    Keep the extracted words/phrases consistent with the content in the order they
                    appear. Separate by new line. Each word/phrase is unique.
                  </span>

                  <textarea
                    id="extractWordsCLevel"
                    name="extractWordsCLevel"
                    value={article?.extractWordsCLevel?.join('\n') ?? ''}
                    onChange={handleExtractedWordsChange}
                    rows={15}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                    disabled={isLoading}
                  />
                </div>
              </div>
            </div>
          )}

          {(!country || country.toUpperCase() === 'VI') && (
            <div>
              {/* US Extracted Words */}
              <div>
                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                    US Title
                  </label>
                  <span>{article?.titleUS ?? ''}</span>
                </div>

                <div className="grid grid-cols-2 gap-6 items-start">
                  <div>
                    <label
                      htmlFor="textALevelUS"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Content level A ({getLevelLabel('A')})
                    </label>
                    <textarea
                      id="textALevelUS"
                      name="textALevelUS"
                      value={article?.textALevelUS ?? ''}
                      rows={15}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm mt-10"
                      disabled={true}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="extractWordsALevelUS"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Title and content level A ({getLevelLabel('A')}) Extracted Words
                    </label>
                    <span className="block text-xs text-gray-500 mb-2">
                      Keep the extracted words/phrases consistent with the content in order they
                      appear. Separate by new line. Each word/phrase is unique.
                    </span>
                    <textarea
                      id="extractWordsALevelUS"
                      name="extractWordsALevelUS"
                      value={article?.extractWordsALevelUS?.join('\n') ?? ''}
                      onChange={handleExtractedWordsChange}
                      rows={15}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-6 items-start">
                  <div>
                    <label
                      htmlFor="textBLevelUS"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Content level B ({getLevelLabel('B')})
                    </label>
                    <textarea
                      id="textBLevelUS"
                      name="textBLevelUS"
                      value={article?.textBLevelUS ?? ''}
                      rows={15}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm mt-10"
                      disabled={true}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="extractWordsBLevelUS"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Title and content level B ({getLevelLabel('B')}) Extracted Words
                    </label>
                    <span className="block text-xs text-gray-500 mb-2">
                      Keep the extracted words/phrases consistent with the content in the order they
                      appear. Separate by new line. Each word/phrase is unique.
                    </span>

                    <textarea
                      id="extractWordsBLevelUS"
                      name="extractWordsBLevelUS"
                      value={article?.extractWordsBLevelUS?.join('\n') ?? ''}
                      onChange={handleExtractedWordsChange}
                      rows={15}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-6 items-start">
                  <div>
                    <label
                      htmlFor="textCLevelUS"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Content level C ({getLevelLabel('C')})
                    </label>
                    <textarea
                      id="textCLevelUS"
                      name="textCLevelUS"
                      value={article?.textCLevelUS ?? ''}
                      rows={15}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm mt-10"
                      disabled={true}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="extractWordsCLevelUS"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Title and content level C ({getLevelLabel('C')}) Extracted Words
                    </label>
                    <span className="block text-xs text-gray-500 mb-2">
                      Keep the extracted words/phrases consistent with the content in the order they
                      appear. Separate by new line. Each word/phrase is unique.
                    </span>

                    <textarea
                      id="extractWordsCLevelUS"
                      name="extractWordsCLevelUS"
                      value={article?.extractWordsCLevelUS?.join('\n') ?? ''}
                      onChange={handleExtractedWordsChange}
                      rows={15}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                      disabled={isLoading}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          <div>
            <label htmlFor="slug" className="block text-sm font-medium text-gray-700 mb-1">
              Published at
            </label>
            <span className="text-gray-700">
              {article ? new Date(article?.publishedAt).toLocaleString() : ''}
            </span>
          </div>

          <div>
            <label htmlFor="slug" className="block text-sm font-medium text-gray-700 mb-1">
              Crawled at
            </label>
            <span className="text-gray-700">
              {article ? new Date(article?.createdAt).toLocaleString() : ''}
            </span>
          </div>

          <div>
            <label htmlFor="slug" className="block text-sm font-medium text-gray-700 mb-1">
              Original Source: {article?.originalSource}
            </label>
            <Link
              to={article?.sourceURL || '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline"
            >
              {article?.sourceURL}
            </Link>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <select
                id="category"
                name="category"
                value={article?.category ?? ''}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isLoading}
              >
                <option value="">Select category</option>
                {categories.map(category => (
                  <option key={category.categoryCode} value={category.categoryCode}>
                    {category.categoryNameEN}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex gap-4 pt-4 border-t">
            <Button type="button" onClick={handleSave} disabled={isLoading} className="flex-1">
              {isLoading ? 'Saving...' : 'Save Draft'}
            </Button>
            <Button
              type="button"
              onClick={handleApprove}
              disabled={isLoading}
              className="flex-1 bg-green-600 hover:bg-green-700"
            >
              Approve Extracted Words and Phrases
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate('/editor/articles', { state: { activeTab: 'words' } })}
              disabled={isLoading}
            >
              Cancel
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};
